import { fbt } from 'fbtee'
import { Heart, MapPin } from 'lucide-react'
import invariant from 'tiny-invariant'
import { Skeleton } from '../../components/ui/skeleton'
import { ArtistGenreEyebrow } from '../../components/ArtistGenreEyebrow'
import { cn } from '../../lib/utils'
import { focalPosition, type FocalAnchor } from '../../lib/focalPosition'

interface Props {
  loading?: boolean
  name?: string
  imageUrl?: string
  imageFocal?: FocalAnchor
  country?: string
  genres?: string[]
  interested?: boolean
  onToggle?: () => void
}

function HeartButton({ interested, onToggle, onImage }: { interested: boolean; onToggle: () => void; onImage: boolean }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="shrink-0 cursor-pointer p-1.5"
      aria-label={fbt('Toggle interest', 'Accessible label for the artist interest toggle on the artist page')}
    >
      <Heart
        className={cn(
          'size-7',
          interested ? 'fill-interested text-interested' : onImage ? 'text-foreground/80' : 'text-muted-foreground',
        )}
      />
    </button>
  )
}

export function ArtistHero({ loading, name, imageUrl, imageFocal, country, genres, interested = false, onToggle }: Props) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-64 -mt-4 -mx-4 rounded-none md:mx-0 md:mt-0 md:rounded-xl" />
        <Skeleton className="h-7 w-48" />
      </div>
    )
  }
  invariant(name, 'ArtistHero: name is required when not loading')
  invariant(onToggle, 'ArtistHero: onToggle is required when not loading')

  const title = (
    <div className="flex-1 min-w-0 space-y-1">
      <ArtistGenreEyebrow artists={genres ? [{ genres }] : undefined} />
      <h1 className="text-2xl font-semibold tracking-tight break-words leading-tight text-foreground">{name}</h1>
      {country && (
        <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="size-3" aria-hidden />
          {country}
        </p>
      )}
    </div>
  )

  if (!imageUrl) {
    return (
      <div className="flex items-start gap-3">
        {title}
        <HeartButton interested={interested} onToggle={onToggle} onImage={false} />
      </div>
    )
  }

  return (
    // Full-bleed on mobile like the event page header; a rounded card once there's room around it.
    <div className="relative h-64 overflow-hidden bg-card -mt-4 -mx-4 md:mx-0 md:mt-0 md:rounded-xl">
      <div
        className="absolute inset-0 bg-cover"
        style={{ backgroundImage: `url(${imageUrl})`, backgroundPosition: focalPosition(imageFocal, '50% 25%') }}
        role="img"
        aria-label={name}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
      <div className="absolute inset-x-0 bottom-0 flex items-end gap-3 p-4 pb-3">
        {title}
        <HeartButton interested={interested} onToggle={onToggle} onImage />
      </div>
    </div>
  )
}
